import { z } from 'zod/v4';

import { SourcingContextRuntype } from '../dataSource/dataSourceBackendTypes';
import { endpoint } from '../http/endpoint';
import { generalOfferEndpointsToInvalidate } from '../offer/offerEndpoints';
import { scenarioDemandEndpointsToInvalidate } from '../sourcingScenario/sourcingScenarioEndpoints';
import { BulkGetDemandGroupsRequestRuntype, DemandGroupDTORuntype } from './demandGroupBackendTypes';

const DemandInputDtoRuntype = z.object({
    type: z.enum(['gross', 'net']),
    rfq_id: z.string(),
    context: z.string().nullable().optional(),
    assembly_id: z.string(),
    quantity: z.number().int().positive(),
    ship_to_site_number: z.string().nullable().optional(),
    ship_to_site_id: z.string().nullable().optional(),
    supplier_site_number: z.string().nullable().optional(),
    supplier_site_id: z.string().nullable().optional(),
    production_start_date: z.string().nullable().optional(),
    delivery_start_date: z.string().nullable().optional(),
    delivery_end_date: z.string().nullable().optional(),
    end_customer_number: z.string().nullable().optional(),
    end_customer_id: z.string().nullable().optional(),
});

const DemandGroupDemandEntryRuntype = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('New'), data: DemandInputDtoRuntype }),
    z.object({ kind: z.literal('Existing'), data: z.string() }),
]);

export const demandGroupEndpoints = {
    'POST /demand-groups/bulk': endpoint({
        description: 'Bulk get demand groups by IDs or project',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: BulkGetDemandGroupsRequestRuntype,
        responseBody: z.object({
            items: z.array(DemandGroupDTORuntype),
        }),
    }),

    'POST /demand-groups': endpoint({
        description: 'Creates a demand group',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            name: z.string(),
            description: z.string().nullable().optional(),
            project_id: SourcingContextRuntype,
            demands: z.array(DemandGroupDemandEntryRuntype),
        }),
        responseBody: DemandGroupDTORuntype,
        invalidates: ['POST /demand-groups/bulk'],
    }),

    'GET /demand-groups/:id': endpoint({
        description: 'Gets a single demand group',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: DemandGroupDTORuntype,
    }),

    'PATCH /demand-groups/:id': endpoint({
        description: 'Updates a demand group',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({
            name: z.string().optional(),
            description: z.union([z.string(), z.null()]).optional(),
            demands: z.array(DemandGroupDemandEntryRuntype).optional(),
        }),
        responseBody: DemandGroupDTORuntype,
        // A group's demands are its scenarios' demands.
        invalidates: [
            'POST /demand-groups/bulk',
            ...scenarioDemandEndpointsToInvalidate,
            'GET /sourcing/progress/:rfqId',
        ],
    }),

    'DELETE /demand-groups/:id': endpoint({
        description: 'Deletes a demand group',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.unknown(),
        // The group takes its sourcing scenarios with it, and a negotiation
        // lists those by id, so it has to be refetched alongside the groups.
        invalidates: ['POST /demand-groups/bulk', 'GET /negotiation-v2', 'GET /negotiation-v2/:id'],
    }),

    'POST /demand-groups/:id/award': endpoint({
        description: 'Awards the given sourcing line items in a demand group',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ lines: z.array(z.string()) }),
        responseBody: z.unknown(),
        // Awarding syncs offers into the award scenario, so invalidate what `POST /sourcing-line-item/sync` does too.
        invalidates: [
            'POST /demand-groups/bulk',
            ...generalOfferEndpointsToInvalidate,
            'GET /sourcing/progress/:rfqId',
            'POST /sourcing/calculation/read-selection',
            'POST /sourcing/calculation/summary',
            'GET /sourcing/calculation/line-items/:id',
            'GET /sourcing-line-item/:id',
        ],
    }),
} satisfies Record<string, Endpoint>;

type Endpoint = ReturnType<typeof endpoint>;
